/**
 * @file 取り込んだ「計画のもと」から、出題の傾向と計画とのつり合いを数える (純関数)。
 *
 * @what: 領域ごとの出題数、語彙ごとの出題数と到達計画の対比、学校ごとの考査の構成を出す。
 * @why: 分析の結果を文章で書き起こすと、本が増えた時点で数字が古くなる。取り込んだ資料から
 *   毎回数えれば、I 巻を足しても別の資料を足しても画面の数字が合い続ける (代表 2026-08-22)。
 *   入試の出題は目標ではなく物差しなので、この画面は「分布に合わせる」ためではなく
 *   「計画の穴を見つける」ために読む。
 *
 *   **単元ではなく語彙 (`分野/単元`) を行にする。** モデルは 1 つの力を段ごとに別の単元へ
 *   分けて持つ (読み聞かせ → 短いお話 → 聞き取り練習 → お話の内容を理解する はすべて
 *   記憶/お話の記憶)。単元を行にすると同じ 33 問が 4 行に重複して見え、連鎖の途中の単元まで
 *   「仕上げに届かない」と誤って印が付く。
 * @failure: 例外を投げない。索引に `分野`・`単元`・`出典校` が無い問は、その軸の集計に載らない。
 */

import type { StudyScanItem } from "../api/scans";
import type { CorpusEntry } from "./corpusIndex";
import { taxonomyKey } from "./corpusIndex";
import type { AttainmentModel, LevelId } from "./types";

/**
 * 水準ではなく機会で見る領域。
 *
 * @why: 集団・運動・制作は初めて見る問題では測れないため、水準を上げるのではなく
 *   「場の点検」で機会の有無を見ると決めた (代表 2026-08-22)。仕上げに届かないことが
 *   計画の穴ではないので、穴の印を付けない。
 */
const MEASURED_BY_PLACE = new Set(["body", "social_communication"]);

const LEVEL_RANK: Record<LevelId, number> = {
  experience: 1,
  introduce: 2,
  practice: 3,
  consolidate: 4,
};

export interface DomainCount {
  domainId: string;
  label: string;
  /** その領域の単元に当たる出題の数 (同じ問を二重に数えない)。 */
  items: number;
}

export interface VocabCalibration {
  /** `分野/単元`。 */
  key: string;
  field: string;
  label: string;
  items: number;
  /** stage id → その語彙を扱う単元のうち最も高い水準。段をまたいだ計画の全体。 */
  levelByStage: Record<string, LevelId>;
  /** その語彙を扱う単元の名前 (段ごとに分かれているので複数になる)。 */
  unitLabels: string[];
  /** どの単元も仕上げに届かない。計画の穴の候補。 */
  shortOfConsolidate: boolean;
  /** 水準ではなく機会で見る領域の語彙 (集団・運動)。仕上げに届かなくてよい。 */
  measuredByPlace: boolean;
  /** リーダーのしぼりこみに渡す値 (`u:{unitId}` か `t:{key}`)。 */
  filterValue: string;
}

export interface SchoolProfile {
  school: string;
  items: number;
  /** 出題された `分野/単元` の種類数。広く問う学校ほど大きい。 */
  unitKinds: number;
  paper: number;
  other: number;
}

export interface CorpusAnalysis {
  totalItems: number;
  sourceTitles: string[];
  schoolCount: number;
  byDomain: DomainCount[];
  vocab: VocabCalibration[];
  schools: SchoolProfile[];
  /** モデルは持っているのに、取り込んだ資料に出題が無い語彙。 */
  vocabularyWithoutItems: string[];
}

/** 索引の `考査` がペーパーかどうか。空欄はペーパー以外に数えない (分からないものを混ぜない)。 */
function isPaper(item: StudyScanItem): boolean {
  return item.index["考査"]?.trim() === "ペーパー";
}

export function analyzeCorpus(
  model: AttainmentModel,
  entries: readonly CorpusEntry[],
): CorpusAnalysis {
  const keyToItems = new Map<string, Set<string>>();
  const schools = new Map<string, { items: CorpusEntry[]; keys: Set<string> }>();
  const titles = new Set<string>();

  for (const e of entries) {
    titles.add(e.sourceTitle);
    const key = taxonomyKey(e.item.index);
    const uid = `${e.sourceId}/${e.item.id}`;
    if (key) {
      const bucket = keyToItems.get(key) ?? new Set<string>();
      bucket.add(uid);
      keyToItems.set(key, bucket);
    }
    const school = e.item.index["出典校"]?.trim();
    if (school) {
      const s = schools.get(school) ?? { items: [], keys: new Set<string>() };
      s.items.push(e);
      if (key) s.keys.add(key);
      schools.set(school, s);
    }
  }

  // 語彙 → それを扱う単元。リーダーのしぼりこみと同じく、最初に見つけた単元へ寄せる。
  const keyToUnits = new Map<
    string,
    { id: string; label: string; domain: string; expected: Record<string, LevelId> }[]
  >();
  for (const u of model.units) {
    for (const t of u.taxonomy) {
      const list = keyToUnits.get(t) ?? [];
      list.push({ id: u.id, label: u.label, domain: u.domain, expected: u.expected });
      keyToUnits.set(t, list);
    }
  }

  const vocab: VocabCalibration[] = [];
  for (const [key, ids] of keyToItems) {
    const units = keyToUnits.get(key) ?? [];
    const levelByStage: Record<string, LevelId> = {};
    for (const u of units) {
      for (const [sid, lv] of Object.entries(u.expected)) {
        const cur = levelByStage[sid];
        if (!cur || LEVEL_RANK[lv] > LEVEL_RANK[cur]) levelByStage[sid] = lv;
      }
    }
    const [field = "", label = key] = key.split("/");
    const byPlace = units.length > 0 && units.every((u) => MEASURED_BY_PLACE.has(u.domain));
    vocab.push({
      key,
      field,
      label,
      items: ids.size,
      levelByStage,
      unitLabels: units.map((u) => u.label),
      shortOfConsolidate:
        units.length > 0 && !byPlace && !Object.values(levelByStage).includes("consolidate"),
      measuredByPlace: byPlace,
      filterValue: units[0] ? `u:${units[0].id}` : `t:${key}`,
    });
  }
  vocab.sort((a, b) => b.items - a.items || a.label.localeCompare(b.label, "ja"));

  // 領域は同じ問を二重に数えない (1 つの問が複数の単元に当たっても、領域ごとに 1 回)。
  const perDomain = new Map<string, Set<string>>();
  for (const u of model.units) {
    for (const t of u.taxonomy) {
      const ids = keyToItems.get(t);
      if (!ids) continue;
      const d = perDomain.get(u.domain) ?? new Set<string>();
      for (const id of ids) d.add(id);
      perDomain.set(u.domain, d);
    }
  }
  const byDomain: DomainCount[] = model.domains
    .map((d) => ({ domainId: d.id, label: d.label, items: perDomain.get(d.id)?.size ?? 0 }))
    .filter((d) => d.items > 0);

  const schoolProfiles: SchoolProfile[] = [...schools.entries()]
    .map(([school, s]) => {
      const paper = s.items.filter((e) => isPaper(e.item)).length;
      return {
        school,
        items: s.items.length,
        unitKinds: s.keys.size,
        paper,
        other: s.items.length - paper,
      };
    })
    .sort((a, b) => b.items - a.items || a.school.localeCompare(b.school, "ja"));

  const known = new Set<string>();
  for (const u of model.units) for (const t of u.taxonomy) known.add(t);
  const vocabularyWithoutItems = [...known].filter((t) => !keyToItems.has(t)).sort();

  return {
    totalItems: entries.length,
    sourceTitles: [...titles].sort(),
    schoolCount: schools.size,
    byDomain,
    vocab,
    schools: schoolProfiles,
    vocabularyWithoutItems,
  };
}
